import React, { useCallback, useEffect, useState } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { AnySchema } from 'yup';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { SxStyle } from '../../../types';
import { Option } from '../../../types';
import MenuItem from '@mui/material/MenuItem';
import { InputWithValidateProps } from './text-input-with-validate';
import { useFieldValidation } from '../../../hooks/formValidate';

export type SelectOptions = {
  options: Option[];
  optionsText: string[];
};

const styles: SxStyle = {
  // TODO Remove?
  // formControl: {
  //   minWidth: '200px',
  // },
};

export type SelectWithValidateProps<T> = SelectProps &
  InputWithValidateProps<T> & {
    validation?: AnySchema;
    value?: Option;
    options: Option[];
    optionsText: string[];
    onValue?: (key: keyof T & string, value: Option | null) => void;
    name: keyof T & string;
  };

function SelectWithValidate<T>({
  value,
  name,
  label,
  onValue,
  onValidation,
  validation,
  fullWidth,
  hideValidationError,
  options,
  optionsText,
  ...props
}: SelectWithValidateProps<T>) {
  const { strings } = useStringsContext();
  const { doValidate, errorMessage } = useFieldValidation();
  const [controlledValue, setControlledValue] = useState<Option>(value ?? '');

  useEffect(() => {
    setControlledValue(value ?? '');
  }, [value]);

  useEffect(() => {
    const validate = async () => {
      if (validation) {
        const isValid = await doValidate(controlledValue, validation);
        onValidation?.(name, isValid, validation);
      }
    };
    validate();
  }, [controlledValue, doValidate, name, onValidation, validation]);

  const onChangeEvent = useCallback(
    (event: SelectChangeEvent<Option>) => {
      const { value } = event.target;
      setControlledValue(value);
      if (onValue) {
        onValue(name, value);
      }
    },
    [name, onValue]
  );

  const renderOption = useCallback(
    (option: Option, index: number) => {
      const text = optionsText ? optionsText[index] : option;
      return (
        <MenuItem key={`${name}-item-${index}`} value={String(option)}>
          {text === '' ? <em>{strings.messages.none}</em> : text}
        </MenuItem>
      );
    },
    [name, optionsText, strings]
  );

  return (
    <FormControl
      variant="outlined"
      sx={
        {
          ...styles.formControl,
          ...props.sx,
        } as SxStyle
      }
      fullWidth={fullWidth}
    >
      <InputLabel id={`${name}-label`}>{label}</InputLabel>
      <Select
        value={controlledValue as Option}
        color="primary"
        label={label}
        onChange={(event) => onChangeEvent(event as SelectChangeEvent<Option>)}
        error={!hideValidationError && !!errorMessage}
        {...props}
      >
        {options.map((option, index) => renderOption(option, index))}
      </Select>
      <FormHelperText error>{!hideValidationError ? errorMessage : null}</FormHelperText>
    </FormControl>
  );
}

export default SelectWithValidate;
